import React, { forwardRef } from 'react';
import type { ElementType } from 'react';
import { StyledSurface } from './HBSurface.styles';
import {
    buildBaseCss,
    buildSpacingCss,
    buildSizingCss,
    buildChildCss,
    buildDecorativeCss,
    pickBaseProps,
    pickSpacingProps,
    pickSizingProps,
    pickChildProps,
    withDisplayName
} from '../../styles/layout/layout.utils';
import type { HBSurfaceProps } from './HBSurface.types';
import type {
    StrictPolymorphicProps,
    PolymorphicRef
} from '../../styles/layout/layout.types';

type HBSurfacePolymorphicProps<C extends ElementType = 'div'> =
    StrictPolymorphicProps<C, HBSurfaceProps>;

type HBSurfaceComponent = <C extends ElementType = 'div'>(
    props: HBSurfacePolymorphicProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

/**
 * Preset surface container with a border and rounded corners.
 * Use `variant` to select the background — `'outlined'` uses white, `'subtle'` uses grey.
 * Accepts spacing and child-placement props. For token-constrained decoration (`backgroundColor`, `borderRadius`, `boxShadow`, `border`), use `HBBox` directly.
 */
const HBSurface = forwardRef(
    <C extends ElementType = 'div'>(
        {
            as,
            children,
            variant = 'outlined',
            borderRadius = 'md',
            displayName,
            ...allProps
        }: HBSurfacePolymorphicProps<C>,
        ref: PolymorphicRef<C>
    ) => {
        const [baseProps, afterBase] = pickBaseProps(allProps);
        const [spacingProps, afterSpacing] = pickSpacingProps(afterBase);
        const [sizingProps, afterSizing] = pickSizingProps(afterSpacing);
        const [childProps, afterChild] = pickChildProps(afterSizing);
        // Defense in depth: `className` and `style` are omitted from
        // HBSurfaceProps, but styled-components still injects a runtime
        // `className` prop when this component is wrapped via
        // `styled(HBSurface)`, and a consumer could still pass `style` via
        // `as any`/JS, regardless of the declared type. Strip both explicitly
        // so neither has any visual effect.
        const {
            className: _discardedClassName,
            style: _discardedStyle,
            ...rest
        } = afterChild as typeof afterChild & {
            className?: string;
            style?: React.CSSProperties;
        };
        const layoutCss = [
            ...buildBaseCss(baseProps),
            ...buildSpacingCss(spacingProps),
            ...buildSizingCss(sizingProps),
            ...buildChildCss(childProps),
            ...buildDecorativeCss({ borderRadius })
        ];

        return withDisplayName(
            displayName,
            <StyledSurface
                as={as || 'div'}
                ref={ref}
                $variant={variant}
                $layoutCss={layoutCss}
                data-variant={variant}
                data-component-name={displayName}
                {...rest}
            >
                {children}
            </StyledSurface>
        );
    }
) as HBSurfaceComponent;

(HBSurface as React.FC).displayName = 'HBSurface';

export default HBSurface;
